import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery } from "convex/react";
import { api } from "../../../convex/_generated/api";
import type { Id } from "../../../convex/_generated/dataModel";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Share2, ScrollText, Trash2, Loader2, Eye, Pencil } from "lucide-react";
import { format } from "date-fns";

type Props = {
  userId: Id<"users">;
  sessionId: Id<"recordingSessions">;
};

/**
 * Logs a "view" audit event once when a non-owner opens a session.
 * The Convex mutation itself no-ops for owners, so this is safe to always render.
 */
export function SessionViewLogger({ userId, sessionId }: Props) {
  const logView = useMutation(api.recordingSessions.logView);
  const loggedRef = useRef<string>("");

  useEffect(() => {
    const key = `${userId}:${sessionId}`;
    if (loggedRef.current === key) return;
    loggedRef.current = key;
    logView({ id: sessionId, userId }).catch(() => {});
  }, [userId, sessionId, logView]);

  return null;
}

const ACTION_LABEL: Record<string, string> = {
  view: "Viewed",
  edit: "Edited",
  generate: "Generated document",
  share_add: "Shared",
  share_update: "Updated sharing",
  share_remove: "Removed sharing",
  delete: "Deleted",
};

export function ShareSessionDialog({ userId, sessionId }: Props) {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [target, setTarget] = useState<string>("");
  const [permission, setPermission] = useState<"view" | "edit">("view");
  const [busy, setBusy] = useState(false);

  const access = useQuery(api.recordingSessions.getAccess, {
    id: sessionId,
    userId,
  });
  const members = useQuery(
    api.teams.listMembers,
    open ? { userId } : "skip",
  );
  const shares = useQuery(
    api.teams.listSharesForSession,
    open ? { userId, sessionId } : "skip",
  );

  const shareSession = useMutation(api.teams.shareSession);
  const unshareSession = useMutation(api.teams.unshareSession);

  if (!access?.canManageShares) return null;

  const ownerId = access.session?.userId;
  const sharedIds = new Set((shares || []).map((s) => s.sharedWithUserId));
  const shareable = (members || []).filter(
    (m) =>
      m.userId !== ownerId &&
      m.accessStatus === "active" &&
      !sharedIds.has(m.userId),
  );

  const handleShare = async () => {
    if (!target) {
      toast({ title: "Pick a teammate", variant: "destructive" });
      return;
    }
    setBusy(true);
    try {
      await shareSession({
        userId,
        sessionId,
        targetUserId: target as Id<"users">,
        permission,
      });
      toast({ title: "Session shared" });
      setTarget("");
    } catch (err: any) {
      toast({
        title: "Couldn't share",
        description: err?.message || "Try again.",
        variant: "destructive",
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2" data-testid="button-share-session">
          <Share2 className="w-4 h-4" />
          Share
        </Button>
      </DialogTrigger>
      <DialogContent data-testid="dialog-share">
        <DialogHeader>
          <DialogTitle>Share session</DialogTitle>
          <DialogDescription>
            Give teammates view or edit access to this session.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="flex flex-col sm:flex-row gap-2">
            <Select value={target} onValueChange={setTarget}>
              <SelectTrigger className="flex-1" data-testid="select-share-target">
                <SelectValue placeholder="Choose teammate" />
              </SelectTrigger>
              <SelectContent>
                {shareable.length === 0 ? (
                  <SelectItem value="none" disabled>
                    No teammates available
                  </SelectItem>
                ) : (
                  shareable.map((m) => (
                    <SelectItem key={m.userId} value={m.userId}>
                      {m.name || m.email}
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
            <Select value={permission} onValueChange={(v) => setPermission(v as "view" | "edit")}>
              <SelectTrigger className="w-full sm:w-28" data-testid="select-share-permission">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="view">View</SelectItem>
                <SelectItem value="edit">Edit</SelectItem>
              </SelectContent>
            </Select>
            <Button onClick={handleShare} disabled={busy} data-testid="button-confirm-share">
              {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Share
            </Button>
          </div>

          <div className="space-y-2">
            <div className="text-sm font-medium text-muted-foreground">
              People with access
            </div>
            {(shares || []).length === 0 ? (
              <p className="text-sm text-muted-foreground" data-testid="text-no-shares">
                Not shared with anyone yet.
              </p>
            ) : (
              (shares || []).map((s) => (
                <div
                  key={s._id}
                  className="flex items-center justify-between gap-2 rounded-md border border-border p-2"
                  data-testid={`row-share-${s.sharedWithUserId}`}
                >
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">
                      {s.name || s.email}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary" className="gap-1">
                      {s.permission === "edit" ? (
                        <Pencil className="w-3 h-3" />
                      ) : (
                        <Eye className="w-3 h-3" />
                      )}
                      {s.permission}
                    </Badge>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      onClick={async () => {
                        try {
                          await unshareSession({
                            userId,
                            sessionId,
                            targetUserId: s.sharedWithUserId,
                          });
                        } catch (err: any) {
                          toast({
                            title: "Couldn't remove",
                            description: err?.message || "Try again.",
                            variant: "destructive",
                          });
                        }
                      }}
                      data-testid={`button-unshare-${s.sharedWithUserId}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function AuditViewerDialog({ userId, sessionId }: Props) {
  const [open, setOpen] = useState(false);
  const access = useQuery(api.recordingSessions.getAccess, {
    id: sessionId,
    userId,
  });
  const events = useQuery(
    api.teams.listAuditForSession,
    open ? { userId, sessionId } : "skip",
  );

  if (!access?.canViewAudit) return null;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2" data-testid="button-view-audit">
          <ScrollText className="w-4 h-4" />
          Activity
        </Button>
      </DialogTrigger>
      <DialogContent data-testid="dialog-audit">
        <DialogHeader>
          <DialogTitle>Activity log</DialogTitle>
          <DialogDescription>
            Who viewed, edited, generated, or shared this session.
          </DialogDescription>
        </DialogHeader>
        <div className="max-h-[60vh] overflow-y-auto space-y-1">
          {events === undefined ? (
            <div className="flex justify-center py-6">
              <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
            </div>
          ) : events.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4 text-center" data-testid="text-no-audit">
              No activity recorded yet.
            </p>
          ) : (
            events.map((e) => (
              <div
                key={e._id}
                className="flex items-start justify-between gap-3 rounded-md border border-border p-2 text-sm"
                data-testid={`row-audit-${e._id}`}
              >
                <div className="min-w-0">
                  <span className="font-medium">{e.actorName || "Someone"}</span>{" "}
                  <span className="text-muted-foreground">
                    {ACTION_LABEL[e.action] || e.action}
                  </span>
                  {e.detail && (
                    <div className="text-xs text-muted-foreground truncate">
                      {e.detail}
                    </div>
                  )}
                </div>
                <div className="text-xs text-muted-foreground whitespace-nowrap">
                  {format(new Date(e.createdAt), "MMM d, h:mm a")}
                </div>
              </div>
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function SharedWithMeList({
  userId,
  selectedSessionId,
  onSelect,
}: {
  userId: Id<"users">;
  selectedSessionId: Id<"recordingSessions"> | null;
  onSelect: (id: Id<"recordingSessions">) => void;
}) {
  const shared = useQuery(api.teams.listSharedWithMe, { userId });

  if (!shared || shared.length === 0) return null;

  return (
    <div className="border-b border-border" data-testid="section-shared-with-me">
      <div className="px-4 pt-3 pb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-1.5">
        <Share2 className="w-3.5 h-3.5" />
        Shared with me
      </div>
      <div className="pb-2">
        {shared.map(({ session, share, ownerName }) => (
          <button
            key={session._id}
            onClick={() => onSelect(session._id)}
            className={`w-full text-left px-4 py-2 hover:bg-muted/60 transition-colors ${
              selectedSessionId === session._id ? "bg-muted" : ""
            }`}
            data-testid={`button-shared-session-${session._id}`}
          >
            <div className="flex items-center justify-between gap-2">
              <span className="text-sm font-medium text-foreground truncate">
                {session.title}
              </span>
              <Badge variant="outline" className="gap-1 shrink-0">
                {share.permission === "edit" ? (
                  <Pencil className="w-3 h-3" />
                ) : (
                  <Eye className="w-3 h-3" />
                )}
                {share.permission}
              </Badge>
            </div>
            <div className="text-xs text-muted-foreground truncate">
              from {ownerName}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
